import type { GenericErrorResponse } from '$lib_commons/models/generic-error-response.ts'
import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { customAlphabet } from 'nanoid'
import { v4 as uuid } from 'uuid'
import { type Writable, get } from 'svelte/store'
import { page } from '$app/state'
import { goto } from '$app/navigation'

let routePrefix: string = ''

export function setRoutePrefix(prefix: string) {
	routePrefix = prefix
}

export function getRoutePrefix(): string {
	return routePrefix
}

export function buildApiUrl(endpointUrl: string): string {
	const cleanPath: string = endpointUrl.startsWith('/') ? endpointUrl : `/${endpointUrl}`
	return `${routePrefix}${cleanPath}`
}

async function fetchREST<T, E = GenericErrorResponse>(
	method: 'GET' | 'POST' | 'PUT' | 'DELETE',
	endpointUrl: string,
	params?: Record<string, string>,
	body?: any,
	headers?: Record<string, string>,
	addRoutePrefix: boolean = true,
	responseType?: AxiosRequestConfig['responseType']
): Promise<AxiosResponse<T>> {
	// Check for missing required query parameters
	if (params) {
		const missingParam: [string, string] | undefined = Object.entries(params).find(
			([_, value]: [string, string]) => !value
		)
		if (missingParam) {
			const [key]: [string, string] = missingParam
			return Promise.reject({
				detail: `${key} parameter is required`,
				status: 400,
				errorCode: 'BAD_REQUEST',
				path: '',
				requestId: ''
			} as GenericErrorResponse)
		}
	}

	// `routePrefix` may contain '/orchestrator', as set via X-Forwarded-Prefix or in layout
	const isAbsoluteUrl: boolean = (() => {
		try {
			new URL(endpointUrl)
			return true
		} catch {
			return false
		}
	})()
	const cleanPath: string = endpointUrl.startsWith('/') ? endpointUrl : `/${endpointUrl}`
	const url: string = isAbsoluteUrl
		? endpointUrl
		: addRoutePrefix
			? `${routePrefix}${cleanPath}`
			: cleanPath

	// Warn if possibly double-prefixed (but skip warning for full URLs)
	if (!isAbsoluteUrl && routePrefix && endpointUrl.startsWith(routePrefix)) {
		console.warn(
			`fetchREST: endpoint already includes prefix '${routePrefix}', which may result in a double prefix. Received: '${endpointUrl}'`
		)
	}

	const config: AxiosRequestConfig = {
		method,
		url,
		params,
		...(responseType && { responseType }),
		headers: {
			...(!(body instanceof FormData) && { 'Content-Type': 'application/json' }),
			...headers
		},
		data: ['POST', 'PUT', 'DELETE'].includes(method)
			? body instanceof URLSearchParams
				? body.toString()
				: body
			: undefined
	}

	if (body instanceof URLSearchParams) {
		;(config.headers as Record<string, string>)['Content-Type'] =
			'application/x-www-form-urlencoded'
	}

	try {
		const response: AxiosResponse<T> = await axios.request<T>(config)

		// If the response body contains a detail field, treat it as an error
		if (responseType !== 'blob' && (response.data as any)?.detail) {
			console.error('API returned an error:', response.data)
			return Promise.reject(response.data)
		}

		return response
	} catch (error: unknown) {
		if (axios.isAxiosError(error)) {
			const err: AxiosError<E> = error
			const errorResponse: E =
				err.response?.data ??
				({
					detail: err.message,
					status: err.response?.status || 500,
					errorCode: 'UNKNOWN',
					path: '',
					requestId: ''
				} as E)
			console.error('Axios request error:', errorResponse)
			return Promise.reject(errorResponse)
		}
		throw error
	}
}

export async function getREST<T, E = GenericErrorResponse>(
	endpointUrl: string,
	params?: Record<string, string>,
	headers?: Record<string, string>,
	addRoutePrefix: boolean = true
): Promise<AxiosResponse<T>> {
	return fetchREST<T, E>('GET', endpointUrl, params, undefined, headers, addRoutePrefix)
}

export async function postREST<T, E = GenericErrorResponse>(
	endpointUrl: string,
	params?: Record<string, string>,
	body?: any,
	headers?: Record<string, string>,
	addRoutePrefix: boolean = true,
	responseType?: AxiosRequestConfig['responseType']
): Promise<AxiosResponse<T>> {
	return fetchREST<T, E>('POST', endpointUrl, params, body, headers, addRoutePrefix, responseType)
}

export async function putREST<T, E = GenericErrorResponse>(
	endpointUrl: string,
	params?: Record<string, string>,
	body?: any,
	headers?: Record<string, string>,
	addRoutePrefix: boolean = true
): Promise<AxiosResponse<T>> {
	return fetchREST<T, E>('PUT', endpointUrl, params, body, headers, addRoutePrefix)
}

export async function deleteREST<T, E = GenericErrorResponse>(
	endpointUrl: string,
	params?: Record<string, string>,
	headers?: Record<string, string>,
	addRoutePrefix: boolean = true,
	body?: any
): Promise<AxiosResponse<T>> {
	return fetchREST<T, E>('DELETE', endpointUrl, params, body, headers, addRoutePrefix)
}

export async function handleApiRequest<T>(request: T, apiCall: (req: T) => Promise<any>) {
	try {
		await apiCall(request)
		return { success: true }
	} catch (error: unknown) {
		console.error('Error:', error)
		return { success: false }
	}
}

const ID_ALPHABET: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

export enum IdGenerationMethod {
	UUID4 = 'UUID4',
	NANOID = 'NANOID'
}

export function generateId(
	size: number = 8,
	idGenerationMethod: IdGenerationMethod = IdGenerationMethod.UUID4
): string {
	switch (idGenerationMethod) {
		case IdGenerationMethod.UUID4:
			return uuid()
		case IdGenerationMethod.NANOID:
			return customAlphabet(ID_ALPHABET, size)()
		default:
			throw new Error(
				`Unknown id generation method: ${idGenerationMethod}. Possible values are: ${Object.values(IdGenerationMethod)}`
			)
	}
}

// Type guard to check if the value has a length property, cuz types does not for instance as it is a Record
function hasLength(value: unknown): value is { length: number } {
	return typeof value === 'object' && value !== null && 'length' in value
}

export async function loadDataIntoStore<T>(
	store: Writable<T | null | undefined>,
	getterFunction: () => Promise<T>
) {
	const storeValue: T | null | undefined = get(store)
	if (!storeValue || (hasLength(storeValue) && storeValue.length === 0)) {
		const data: T = await getterFunction()
		store.set(data)
	}
}
/**
 * Resolves an app-relative path to a full href, accounting for orchestrator vs. standalone mode.
 * Uses the same prefixing rules as `commonsGoTo`.
 *
 * @param path - The relative path (e.g. "/dataset/123")
 * @param currentPath - Optional override for the current pathname (defaults to `page.url.pathname`)
 */
export const commonsResolveHref = (
	path: string,
	currentPath: string = page.url.pathname
): string => {
	if (path.startsWith('?')) {
		return path
	}

	if (!path.startsWith('/')) {
		throw new Error(
			`Invalid path: ${path}. Please use absolute paths when using commonsResolveHref(path).`
		)
	}

	const parts: string[] = currentPath
		.split('/')
		.filter((segment: string): boolean => Boolean(segment))
	const appsIndex: number = parts.indexOf('apps')
	if (appsIndex === -1) {
		return path
	}

	if (parts.length <= appsIndex + 1) {
		throw new Error(
			`Invalid URL: ${currentPath}. It contains apps, but the app name could not be deduced.`
		)
	}

	const appName: string = parts[appsIndex + 1]
	let normalizedPath = path
	if (path.startsWith(`/${appName}`)) {
		normalizedPath = path.slice(appName.length + 1)
	}

	let href: string = `/apps/${appName}${normalizedPath}`
	if (routePrefix) {
		href = `${routePrefix}${href}`
	}
	return href
}

/**
 * Navigates to another URL. Uses the `goto` function from SvelteKit internally, but has some
 * additional logic to handle the case when the app is running inside an orchestrator vs.
 * standalone mode.
 *
 * Two cases:
 * 1. Standalone mode (no runtime prefix is assumed): simply navigates to `path`.
 * 2. Running inside orchestrator (runtime prefix exists):
 *    - If the current URL contains the segment "apps", extract the app name (the segment after "apps")
 *   - Append the app name to the provided path.
 *   - If a `routePrefix` is set, prepend it to the URL. The `routePrefix` is set via the
 *   `setRoutePrefix()` function, which is probably set by the orchestrator/hooks.server.ts.
 *
 * @param path - The relative path to navigate to (e.g. "/assistant/{projectName}")
 * @param opts - Optional parameters for the navigation.
 */
export const commonsGoTo = (
	path: string,
	opts?:
		| {
				replaceState?: boolean | undefined
				noScroll?: boolean | undefined
				keepFocus?: boolean | undefined
				invalidateAll?: boolean | undefined
				invalidate?: (string | URL | ((url: URL) => boolean))[] | undefined
				state?: App.PageState | undefined
		  }
		| undefined
): void => {
	if (path.startsWith('?')) {
		goto(path, opts)
		return
	}

	const href = commonsResolveHref(path)
	if (page.url.pathname.split('/').filter(Boolean).indexOf('apps') === -1) {
		console.log(`Navigating standalone to: ${path}`)
	} else {
		console.log(`Navigating inside orchestrator with app name: ${href}`)
	}
	goto(href, opts)
}

/**
 * Navigates to a **different** app within the orchestrator. Unlike `commonsGoTo`, which always
 * scopes navigation to the current app, this function targets a specific app by name.
 *
 * - Orchestrator mode: navigates to `/apps/{targetApp}/{path}` (with `routePrefix` if set).
 * - Standalone mode: navigates to `/{path}` directly.
 *
 * @param targetApp - The name of the target app (e.g. "session-viewer").
 * @param path - The path within the target app (e.g. "/1" or "/details/42"). Must start with "/".
 * @param opts - Optional SvelteKit navigation parameters.
 */
export const commonsGoToApp = (
	targetApp: string,
	path: string,
	opts?:
		| {
				replaceState?: boolean | undefined
				noScroll?: boolean | undefined
				keepFocus?: boolean | undefined
				invalidateAll?: boolean | undefined
				invalidate?: (string | URL | ((url: URL) => boolean))[] | undefined
				state?: App.PageState | undefined
		  }
		| undefined
): void => {
	if (!path.startsWith('/')) {
		throw new Error(
			`Invalid path: ${path}. Please use absolute paths when using commonsGoToApp(targetApp, path).`
		)
	}

	const currentPath: string = page.url.pathname
	const parts: string[] = currentPath
		.split('/')
		.filter((segment: string): boolean => Boolean(segment))
	const appsIndex: number = parts.indexOf('apps')

	if (appsIndex === -1) {
		console.log(`Navigating standalone to app "${targetApp}": ${path}`)
		goto(path, opts)
		return
	}

	let href: string = `/apps/${targetApp}${path}`
	if (routePrefix) {
		href = `${routePrefix}${href}`
	}
	console.log(`Navigating to app "${targetApp}" inside orchestrator: ${href}`)
	goto(href, opts)
}
